import {
  WSEmptyState,
  WSFilters,
  WSInfiniteScroll,
  WSList,
  WSTableColumnSortDirection,
  WSTableItem,
  WSTableToolbar
} from "@wingspanhq/fe-component-library";
import { Transaction } from "@wingspanhq/wingspan-api-sdk";
import { useCallback, useMemo, useState } from "react";
import { queriesExternalBankAccounts, queriesTransactions } from "@query";
import { useUrlQuery } from "../../../../shared/hooks/useUrlQuery";
import {
  useBulkDeleteTransactions,
  useBulkRenameTransactions,
  useBulkSetAmountTransactions,
  useBulkSetBusinessTransactions,
  useBulkSetTypeTransactions,
  useBulkUpdateCategoryTransactions
} from "../../hooks/useTransactionBulkActions";
import { useModalTransactionDetails } from "../../modals/ModalTransactionDetails";
import {
  TYPE_FILTER_ALL,
  useTransactionsFiltersConfig
} from "./filters/config";
import { createListTransactionsRequest } from "./filters/request";
import { TransactionsFilter, TransactionsSortField } from "./filters/types";
import { TableTransactions } from "./TableTransactions";

export const FilterableTableTransactions: React.FC = () => {
  const { urlQuery, setUrlQuery } = useUrlQuery<
    TransactionsFilter,
    TransactionsSortField
  >();

  const modalTransactionDetails = useModalTransactionDetails();

  const [selection, setSelection] = useState<WSTableItem<Transaction>[]>([]);

  const bulkRename = useBulkRenameTransactions();
  const bulkSetAmount = useBulkSetAmountTransactions();
  const bulkSetType = useBulkSetTypeTransactions();
  const bulkUpdateCategory = useBulkUpdateCategoryTransactions();
  const bulkSetBusiness = useBulkSetBusinessTransactions();
  const bulkDelete = useBulkDeleteTransactions();

  const filtersConfig = useTransactionsFiltersConfig();

  const request = useMemo(
    () => createListTransactionsRequest(urlQuery.filter, urlQuery.sort),
    [urlQuery.filter, urlQuery.sort]
  );

  const queryTransactions =
    queriesTransactions.listTransactions.usePaginatedQuery(request);

  const queryAccounts =
    queriesExternalBankAccounts.listExternalBankAccounts.useQueryAll();

  const accountNameById = useMemo(() => {
    const map = new Map<string, string>();
    (queryAccounts.flatData ?? []).forEach(account => {
      map.set(account.id, account.nickname || account.accountName);
    });
    return map;
  }, [queryAccounts.flatData]);

  const transactions = queryTransactions.flatData ?? [];

  const totalCount = queryTransactions.data?.pages?.[0]?.pagination?.totalSize;

  const filterValues = useMemo(
    () => ({
      type: urlQuery.filter.type,
      category: urlQuery.filter.category
    }),
    [urlQuery.filter.type, urlQuery.filter.category]
  );

  const hasFilters = useMemo(
    () =>
      (!!urlQuery.filter.type && urlQuery.filter.type !== TYPE_FILTER_ALL) ||
      !!urlQuery.filter.category,
    [urlQuery.filter.type, urlQuery.filter.category]
  );

  const onSortByDate = useCallback(
    (direction: WSTableColumnSortDirection) => {
      setUrlQuery(prev => ({
        ...prev,
        sort: { field: "date", direction }
      }));
    },
    [setUrlQuery]
  );

  const runBulk = async (
    action: (transactions: Transaction[]) => Promise<boolean>
  ) => {
    const ran = await action(selection.map(item => item.data));
    if (ran) {
      setSelection([]);
    }
  };

  const bulkActions = [
    { label: "Rename", onClick: () => void runBulk(bulkRename) },
    { label: "Set amount", onClick: () => void runBulk(bulkSetAmount) },
    { label: "Set type", onClick: () => void runBulk(bulkSetType) },
    {
      label: "Mark deductible",
      onClick: () => void runBulk(t => bulkSetBusiness(t, true))
    },
    {
      label: "Mark not deductible",
      onClick: () => void runBulk(t => bulkSetBusiness(t, false))
    },
    {
      label: "Update category",
      onClick: () => void runBulk(bulkUpdateCategory)
    },
    { label: "Delete", onClick: () => void runBulk(bulkDelete) }
  ];

  const renderContent = () => {
    if (queryTransactions.isLoading) {
      return (
        <TableTransactions
          transactions={[]}
          accountNameById={accountNameById}
          loading
          onSortByDate={onSortByDate}
        />
      );
    }

    if (queryTransactions.isError) {
      return (
        <WSEmptyState
          type="error"
          title="Couldn't load transactions"
          description="Something went wrong loading your transactions. Please try again."
          actions={{
            buttons: [
              {
                label: "Try again",
                kind: "Secondary",
                onClick: () => {
                  void queryTransactions.refetch();
                }
              }
            ]
          }}
        />
      );
    }

    if (transactions.length === 0) {
      return hasFilters ? (
        <WSEmptyState
          type="search"
          title="No matching transactions"
          description="No transactions match your filters. Try clearing or changing them."
        />
      ) : (
        <WSEmptyState
          type="documents"
          title="No transactions yet"
          description="Once transactions are synced or added, they'll appear here."
        />
      );
    }

    return (
      <WSInfiniteScroll
        onLoad={() => queryTransactions.fetchNextPage()}
        loadMore={transactions.length > 0}
        endOfList={!queryTransactions.hasNextPage}
        loading={!!queryTransactions.isFetchingNextPage}
      >
        <TableTransactions
          transactions={transactions}
          accountNameById={accountNameById}
          dateDirection={urlQuery.sort?.direction}
          onSortByDate={onSortByDate}
          selection={selection}
          onSelectionChange={setSelection}
          onRowClick={transaction => {
            void modalTransactionDetails.open({ transaction });
          }}
        />
      </WSInfiniteScroll>
    );
  };

  return (
    <WSList gap="2XL">
      <WSFilters<TransactionsFilter>
        values={filterValues}
        onFilter={newFilter => {
          setUrlQuery(prev => ({
            ...prev,
            filter: { ...prev.filter, ...newFilter }
          }));
        }}
        primaryFilters={filtersConfig}
        info={totalCount === undefined ? undefined : { count: totalCount }}
      />

      {selection.length > 0 && (
        <WSTableToolbar
          count={selection.length}
          actionsLabel="Bulk actions"
          actions={bulkActions}
          onClear={() => setSelection([])}
        />
      )}

      {renderContent()}
    </WSList>
  );
};
